import { InputOTPGenerator } from '@/components/inputs/otp-generator'
import { InlineLoader } from '@/components/loader/custom-loader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import TogglePassword from '@/components/ui/toggle-password'
import { resetAuth } from '@/redux/auth/authSlice'
import {
  useLoginMutation,
  useVerifyLoginOTPMutation,
  useVerifyMFAOTPMutation,
} from '@/redux/services/auth/authApi'
import { useAppDispatch } from '@/redux/store'
import type { Dictionary } from '@/types'
import routesPath from '@/utils/routesPath'
import { loginSchema } from '@/validation-schemas/auth/login'
import { useFormik } from 'formik'
import Cookies from 'js-cookie'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import AuthLayout from '../layout'

const { FORGOT_PASSWORD, DASHBOARD } = routesPath
export default function LoginPage() {
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [verifyPassword, setVerifyPassword] = useState(false)
  const [OTP, setOTP] = useState<any>('')
  const [loginCredentials, setLoginCredentials] = useState<Dictionary | null>(
    null,
  )

  const [login, { isLoading, data }] = useLoginMutation()
  const [verifyOTP, { isLoading: isVerifyingOTP }] = useVerifyMFAOTPMutation()
  const [verifyLoginOTP, { isLoading: isVerifyingLoginOTP }] =
    useVerifyLoginOTPMutation()

  const dispatch = useAppDispatch()

  const handleOTPSubmit = async () => {
    if (data?.data?.requires_otp) {
      verifyLoginOTP({ code: OTP })
        .unwrap()
        .then(() => {
          navigate(DASHBOARD)
        })
        .catch((error: any) => {
          toast.error(
            error.data?.message || error.message || 'Failed to verify OTP',
          )
        })
      return
    }
    verifyOTP({ code: OTP })
      .unwrap()
      .then((_) => {
        navigate(DASHBOARD)
      })
      .catch((error: any) => {
        toast.error(
          error.data?.message || error.message || 'Failed to verify OTP',
        )
      })
  }

  const handleFormSubmit = async (values: Dictionary) => {
    login(values)
      .unwrap()
      .then((res) => {
        if (
          res?.data?.user?.mfa?.is_enabled ||
          res?.data?.requires_mfa ||
          res?.data?.requires_otp
        ) {
          setLoginCredentials(values)
          setVerifyPassword(true)
          return
        }
        navigate(DASHBOARD)
      })
      .catch((error: any) => {
        toast.error(error.data?.message || error.message)
      })
  }

  const handleResendOTP = async () => {
    if (!loginCredentials) return

    try {
      const res = await login(loginCredentials).unwrap()
      if (
        res?.data?.user?.mfa?.is_enabled ||
        res?.data?.requires_mfa ||
        res?.data?.requires_otp
      ) {
        toast.success('OTP has been resent successfully')
        setOTP('')
      }
    } catch (error: any) {
      toast.error(
        error.data?.message || error.message || 'Failed to resend OTP',
      )
    }
  }

  const { handleChange, handleSubmit, touched, errors, isValid, dirty } =
    useFormik({
      initialValues: {
        email: '',
        password: '',
      },
      validationSchema: loginSchema,
      onSubmit: handleFormSubmit,
    })

  return (
    <AuthLayout
      title='Welcome Back!'
      subTitle='Please sign in to access the dashboard'
      hasBack={verifyPassword}
      onBack={() => {
        dispatch(resetAuth())
        setVerifyPassword(false)
        setLoginCredentials(null)
        setOTP('')
        Cookies.remove('token')
      }}
    >
      {!verifyPassword ? (
        <form onSubmit={handleSubmit}>
          <div className='flex flex-col gap-3'>
            <Input
              id='email'
              label='Email'
              name='email'
              onChange={handleChange}
              touched={touched.email}
              error={errors.email}
              placeholder='admin@example.com'
              hideBorderError
            />
            <div className='relative'>
              <Input
                id='password'
                label='Password'
                name='password'
                onChange={handleChange}
                touched={touched.password}
                error={errors.password}
                type={showPassword ? 'text' : 'password'}
                placeholder='Enter your password'
                hideBorderError
              />
              <TogglePassword
                showPassword={showPassword}
                setShowPassword={setShowPassword}
              />
            </div>
          </div>
          <span className='text-xs text-[#A3A3A3] block text-right pt-3'>
            <Link to={FORGOT_PASSWORD} className='text-red-500'>
              Forgot Password?
            </Link>
          </span>

          <div className='my-4 mt-10'>
            <Button
              type='submit'
              loading={isLoading}
              disabled={isLoading || !(isValid && dirty)}
            >
              Login
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleOTPSubmit}>
          <div className='flex flex-col gap-3'>
            <InputOTPGenerator length={6} onChange={(code) => setOTP(code)} />
          </div>
          <span className='flex gap-1 items-center text-xs text-[#A3A3A3] justify-center text-center pt-3'>
            <span>Didn’t recieve code?</span>
            <span
              className={`text-green-700 cursor-pointer ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={isLoading ? undefined : handleResendOTP}
            >
              Resend code
            </span>
            {isLoading && <InlineLoader />}
          </span>

          <div className='my-4 mt-10'>
            <Button
              type='button'
              onClick={handleOTPSubmit}
              loading={isVerifyingOTP || isVerifyingLoginOTP}
              disabled={isVerifyingOTP || OTP.length < 6 || isVerifyingLoginOTP}
            >
              Verify Code
            </Button>
          </div>
        </form>
      )}
    </AuthLayout>
  )
}
